import type { FC, ReactNode } from 'react'

import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { Hand, MousePointerClick, Move3d } from 'lucide-react'
import { cn } from '@/lib/utils'
import { glass } from '@/components/datacenter/hud/primitives'

// A short how-to that fades away after a few seconds or the first interaction.
const Hints: FC = (): ReactNode => {
    const [visible, setVisible] = useState(true)
    const [touch, setTouch] = useState(false)

    useEffect(() => {
        setTouch(window.matchMedia('(pointer: coarse)').matches)

        const hide = () => setVisible(false)
        const timer = window.setTimeout(hide, 9000)

        window.addEventListener('pointerdown', hide, { once: true })
        window.addEventListener('wheel', hide, { once: true })

        return () => {
            window.clearTimeout(timer)
            window.removeEventListener('pointerdown', hide)
            window.removeEventListener('wheel', hide)
        }
    }, [])

    const items = touch
        ? [
            { icon: Hand, text: 'Drag to look around' },
            { icon: Move3d, text: 'Pinch to move closer' },
            { icon: MousePointerClick, text: 'Tap a rack to open it' }
        ]
        : [
            { icon: Hand, text: 'Drag to look around' },
            { icon: Move3d, text: 'Scroll to walk in' },
            { icon: MousePointerClick, text: 'Click a rack to open it' }
        ]

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    className='pointer-events-none absolute left-1/2 top-24 z-10 hidden -translate-x-1/2 sm:block'
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0, transition: { delay: 0.8 } }}
                    exit={{ opacity: 0, y: -10 }}
                >
                    <ul className={cn(glass, 'flex items-center gap-4 rounded-full px-4 py-2 text-xs text-slate-300')}>
                        {items.map(item => {
                            const Icon = item.icon

                            return (
                                <li key={item.text} className='flex items-center gap-1.5 whitespace-nowrap'>
                                    <Icon className='size-3.5 text-cyan-300' aria-hidden='true' />
                                    {item.text}
                                </li>
                            )
                        })}
                    </ul>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default Hints
